import { useEffect, useRef, useState } from "react";
import { server } from "../../server";
import axios from "axios";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import Select from "react-select";
import { generateRandomNo, getSession } from "../../functions";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { getDecodedToken } from "../../auth";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const drawCircularImage = (doc, dataUrl, format, cx, cy, r) => {
  doc.saveGraphicsState();
  doc.circle(cx, cy, r, null);
  doc.clip();
  doc.discardPath();
  doc.addImage(dataUrl, format, cx - r, cy - r, r * 2, r * 2);
  doc.restoreGraphicsState();
};

function VisitorBook() {
  const [visitorBookDetails, setVisitorBookDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const recordsPerPage = 10;
  const [showCamera, setShowCamera] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const [activeReport, setActiveReport] = useState("single");
  const fromDateInputRef = useRef(null);
  const toDateInputRef = useRef(null);
  const dateInputRef = useRef(null);
  const PICKUP_PURPOSE = "Student Pick-Up / Early Departure";
  const [studentName, setStudentName] = useState("");
  const [studentInfoLoading, setStudentInfoLoading] = useState(false);
  const [studentInfoError, setStudentInfoError] = useState("");
  const [user, setUser] = useState(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const decodedData = getDecodedToken();
    if (decodedData) {
      setUser(decodedData);
    }
  }, []);

  const userRole = user?.roles;
  const userId = user?.userId;

  const openWebcam = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      setCameraStream(stream);
      setShowCamera(true);
    } catch (err) {
      console.error("Error accessing webcam:", err);
    }
  };

  const closeWebcam = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach((track) => track.stop());
    }
    setShowCamera(false);
  };

  const capturePhoto = () => {
    const video = document.getElementById("visitorVideo");
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const imageData = canvas.toDataURL("image/png");
    setCapturedImage(imageData);
    closeWebcam();

    // Convert base64 -> File
    const byteString = atob(imageData.split(",")[1]);
    const mimeString = imageData.split(",")[0].split(":")[1].split(";")[0];
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    const blob = new Blob([ab], { type: mimeString });
    const file = new File([blob], `webcam_${Date.now()}.png`, {
      type: mimeString,
    });

    // Set webcam image into formData
    setFormData((prev) => ({ ...prev, webcam: file }));
  };

  const purposeOptions = [
    "Principal / Head of School Meeting",
    "Parent–Teacher Meeting",
    "Student Pick-Up / Early Departure",
    "Student Admission Inquiry",
    "Admission Documentation",
    "Fee Payment / Deposit",
    "Academic Discussion",
    "Staff Meeting",
    "Administrative Work",
    "Accounts / Finance Related",
    "Library Related",
    "Examination Related",
    "Counseling Session",
    "Marketing / Promotion",
    "Vendor / Service Discussion",
    "Maintenance / Facility Work",
    "School Event / Function",
    "Official Inspection / Audit",
    "Interview",
    "Other",
  ].map((purpose) => ({
    label: purpose,
    value: purpose,
  }));

  const visitorTypeOptions = [
    "Student",
    "Parent / Guardian",
    "Teacher",
    "School Staff",
    "Administrative Staff",
    "Book Supplier",
    "Accounts / Finance",
    "Vendor / Supplier",
    "Maintenance / Service Staff",
    "Interviewer",
    "Education Officer / Inspector",
    "Alumni",
    "Guest",
    "Security",
    "Other",
  ].map((visitorType) => ({
    label: visitorType,
    value: visitorType,
  }));

  const meetingWithOptions = ["Swamiji", "Staff", "Principal", "Other"].map(
    (option) => ({ label: option, value: option }),
  );

  const [formData, setFormData] = useState({
    visitorId: generateRandomNo(),
    name: "",
    phoneNo: "",
    emailId: "",
    visitorType: "",
    purpose: "",
    admissionNo: "",
    meetingWith: "",
    numberOfPersons: "",
    inTime: "",
    outTime: "",
    remark: "",
    document: null,
    webcam: null,
  });

  const fetchStudent = async (admissionNo) => {
    if (!admissionNo) {
      setError("Please enter Admission No");
      return null;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post(
        `${server}/getParticularStudentMongodb`,
        { admission_no: admissionNo },
        {
          headers: {
            Authorization: `${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        },
      );
      const studentData = response.data?.["body-json"]?.body?.student;
      if (studentData) return studentData;

      setError("No Student Data Found");
      return null;
    } catch (err) {
      setError("Student not found or API error.");
      return null;
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "file" ? files[0] : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let uploadedDocumentUrl = "";
      let uploadedWebcamUrl = "";

      // Upload document if present
      if (formData.document) {
        const presignRes = await axios.post(
          "https://7otfgyv0ke.execute-api.ap-south-1.amazonaws.com/prod/visitor_document_presigned_mongodb",
          { fileName: [formData.document.name] },
        );

        const uploadDetails = JSON.parse(presignRes.data["body-json"].body)
          .uploadDetails?.[0];

        if (!uploadDetails || !uploadDetails.signedUrl || !uploadDetails.key) {
          throw new Error("Invalid presigned URL response for document");
        }

        await axios.put(uploadDetails.signedUrl, formData.document, {
          headers: { "Content-Type": formData.document.type },
        });

        uploadedDocumentUrl = uploadDetails.key;
      }

      // Upload webcam photo if present
      if (formData.webcam) {
        const presignRes = await axios.post(
          "https://7otfgyv0ke.execute-api.ap-south-1.amazonaws.com/prod/visitor_document_presigned_mongodb",
          { fileName: [formData.webcam.name] },
        );

        const uploadDetails = JSON.parse(presignRes.data["body-json"].body)
          .uploadDetails?.[0];

        if (!uploadDetails || !uploadDetails.signedUrl || !uploadDetails.key) {
          throw new Error("Invalid presigned URL response for webcam");
        }

        await axios.put(uploadDetails.signedUrl, formData.webcam, {
          headers: { "Content-Type": formData.webcam.type },
        });

        uploadedWebcamUrl = uploadDetails.key;
      }

      const payload = {
        ...formData,
        document: uploadedDocumentUrl,
        webcam: uploadedWebcamUrl,
      };

      await axios.post(`${server}add_visitor_mongodb`, payload, {
        headers: {
          Authorization: `${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
      });
      toast.success("Visitor Saved", { position: "top-right" });

      const closeButton = document.querySelector('[data-bs-dismiss="modal"]');
      if (closeButton) {
        closeButton.click();
      }
      fetchVisitorBookDetails();

      setFormData({
        visitorId: generateRandomNo(),
        name: "",
        phoneNo: "",
        emailId: "",
        visitorType: "",
        purpose: "",
        admissionNo: "",
        meetingWith: "",
        numberOfPersons: "",
        inTime: "",
        outTime: "",
        remark: "",
        document: null,
        webcam: null,
      });

      const fileInput = document.getElementById("documentInput");
      if (fileInput) fileInput.value = "";
    } catch (err) {
      toast.error("Please Try Again", { position: "top-right" });
    } finally {
      setLoading(false);
    }
  };

  const fetchVisitorBookDetails = async () => {
    setLoading(true);
    try {
      const today = new Date().toISOString().split("T")[0];
      const response = await axios.get(`${server}/visitor_alldata_mongodb`, {
        headers: { Authorization: `${localStorage.getItem("token")}` },
      });
      const responseData = response.data?.["body-json"]?.body?.data;

      if (Array.isArray(responseData)) {
        const sortedData = [...responseData].sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
        );
        setVisitorBookDetails(sortedData);
      } else {
        console.error("Invalid data format from API", response.data);
      }
    } catch (error) {
      console.error("Error fetching fee details:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVisitorBookDetails();
  }, []);

  const isPickupPurpose = formData.purpose === PICKUP_PURPOSE;

  useEffect(() => {
    if (!isPickupPurpose) return;

    const admissionNo = (formData.admissionNo || "").trim();

    setStudentName("");
    setStudentInfoError("");

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!admissionNo) return;

    debounceRef.current = setTimeout(async () => {
      setStudentInfoLoading(true);
      try {
        const student = await fetchStudent(admissionNo);
        if (student) {
          // adjust field name according to your API response
          const name =
            student?.name || student?.student_name || student?.full_name || "";
          setStudentName(name);
          if (!name) setStudentInfoError("Student name not found in response.");
        } else {
          setStudentInfoError("No student found for this Admission No.");
        }
      } finally {
        setStudentInfoLoading(false);
      }
    }, 500);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [formData.admissionNo, isPickupPurpose]);

  // Fetches a (possibly cross-origin) image and converts it to a data URL,
  // since jsPDF needs the bytes in hand rather than a bare URL.
  const toDataUrl = async (url) => {
    try {
      const res = await fetch(url);
      const blob = await res.blob();
      return await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (err) {
      console.error("Could not load webcam photo for gate pass:", err);
      return null;
    }
  };

  const handleDownloadPDF = async (visitor) => {
    const session = getSession();
    const doc = new jsPDF("p", "mm", "a4");
    doc.setLineHeightFactor(1.05);

    const marginX = 10;
    const passWidth = 210 - marginX * 2;
    const passHeight = 60;
    const gapY = 12;
    const passesPerPage = 3;
    const cardRadius = 3;

    const accent = [239, 78, 36];
    const borderGray = [225, 225, 225];
    const grayText = [140, 140, 140];
    const darkText = [30, 30, 30];

    for (let i = 0; i < visitor.length; i++) {
      const v = visitor[i];
      const posOnPage = i % passesPerPage;
      if (i > 0 && posOnPage === 0) doc.addPage();

      const x = marginX;
      const y = marginX + posOnPage * (passHeight + gapY);
      const stripWidth = 2; // #3 — thinner left border (was 3.5)
      const contentX = x + stripWidth + 5;
      const contentWidth = passWidth - stripWidth - 11;
      const headerRightX = contentX + contentWidth;

      // Card background + thin neutral border
      doc.setFillColor(255, 255, 255);
      doc.roundedRect(x, y, passWidth, passHeight, cardRadius, cardRadius, "F");
      doc.setDrawColor(...borderGray);
      doc.setLineWidth(0.4);
      doc.roundedRect(x, y, passWidth, passHeight, cardRadius, cardRadius, "S");

      doc.saveGraphicsState();
      doc.roundedRect(
        x,
        y,
        passWidth,
        passHeight,
        cardRadius,
        cardRadius,
        null,
      );
      doc.clip();
      doc.discardPath();
      doc.setFillColor(...accent);
      doc.rect(x, y, stripWidth, passHeight, "F");
      doc.restoreGraphicsState();

      // Webcam photo — circular crop
      const photoR = 7;
      const photoCx = headerRightX - photoR;
      const photoCy = y + 4 + photoR;

      let photoDataUrl = null;
      if (v.webcam && v.webcam.length > 0 && v.webcam !== "[]") {
        photoDataUrl = await toDataUrl(v.webcam);
      }

      if (photoDataUrl) {
        try {
          const format =
            /^data:image\/(\w+);/.exec(photoDataUrl)?.[1]?.toUpperCase() ||
            "PNG";
          drawCircularImage(
            doc,
            photoDataUrl,
            format,
            photoCx,
            photoCy,
            photoR,
          );
        } catch (err) {
          console.error("Could not draw webcam photo on gate pass:", err);
        }
      } else {
        doc.setDrawColor(...borderGray);
        doc.setLineWidth(0.3);
        doc.setFillColor(248, 248, 248);
        doc.circle(photoCx, photoCy, photoR, "FD");
        doc.setFont("helvetica", "normal").setFontSize(6);
        doc.setTextColor(...grayText);
        doc.text("No photo", photoCx, photoCy, {
          align: "center",
          baseline: "middle",
        });
      }

      const titleRightX = photoCx - photoR - 4;

      // Logo — #4 made bigger (radius 4.5 → 5.5)
      const logoR = 5.5;
      drawCircularImage(
        doc,
        "/assets/images/icon.png",
        "PNG",
        contentX + logoR,
        y + 4 + logoR,
        logoR,
      );

      doc.setFont("helvetica", "bold").setFontSize(12);
      doc.setTextColor(...darkText);
      doc.text("Acharyakulam", contentX + logoR * 2 + 4, y + 8);

      // #4 — address and session split into separate lines (address only here)
      doc.setFont("helvetica", "normal").setFontSize(7.5);
      doc.setTextColor(...grayText);
      doc.text("Tata Road, Namkum, Ranchi", contentX + logoR * 2 + 4, y + 12.3);

      // #4 — session now its own 3rd line below the address
      doc.text(`Session ${session || "N/A"}`, contentX + logoR * 2 + 4, y + 15);

      doc.setFont("helvetica", "bold").setFontSize(10);
      doc.setTextColor(...darkText);
      doc.text("Visitor's gate pass", titleRightX, y + 9, { align: "right" });

      doc.setFont("helvetica", "normal").setFontSize(7.5);
      doc.setTextColor(...grayText);
      doc.text(`Pass id: ${v.visitorId || "N/A"}`, titleRightX, y + 13, {
        align: "right",
      });

      // Details grid — gridStartY nudged from 21 to 24 to clear the new 3rd header line
      const colGap = 5;
      const colWidth = (contentWidth - colGap * 2) / 3;
      const rowHeight = 7;
      const gridStartY = y + 24;

      const fields = [
        ["Name", v.name || "N/A"],
        ["Phone no.", v.phoneNo || "N/A"],
        ["Visitor type", v.visitorType || "N/A"],
        ["Purpose", v.purpose || "N/A"],
        ["Meeting with", v.meetingWith || "N/A"],
        ["No. of person", v.numberOfPersons || "N/A"],
        ["In time", v.inTime || "N/A"],
        [
          "Date",
          v.createdAt ? new Date(v.createdAt).toLocaleString("en-GB") : "N/A",
        ],
        ["Admission no.", v.admissionNo || "—"],
      ];

      fields.forEach(([label, value], idx) => {
        const col = idx % 3;
        const row = Math.floor(idx / 3);
        const fx = contentX + col * (colWidth + colGap);
        const fy = gridStartY + row * rowHeight;

        doc.setFont("helvetica", "normal").setFontSize(6.5);
        doc.setTextColor(...grayText);
        doc.text(label, fx, fy);

        doc.setFont("helvetica", "bold").setFontSize(8.5);
        doc.setTextColor(...darkText);
        const lines = doc.splitTextToSize(String(value), colWidth);
        doc.text(lines.slice(0, 1), fx, fy + 3.4);
      });

      // Remark + signature — tinted bar
      const footerY = y + 45;
      const footerHeight = 9;
      doc.setFillColor(248, 248, 248);
      doc.roundedRect(contentX, footerY, contentWidth, footerHeight, 2, 2, "F");

      doc.setFont("helvetica", "normal").setFontSize(7);
      doc.setTextColor(...grayText);
      doc.text("Remark", contentX + 6, footerY + 5);
      const remarkLabelWidth = doc.getTextWidth("Remark   ");

      doc.setFont("helvetica", "bold").setFontSize(8.5);
      doc.setTextColor(...darkText);
      const remarkLines = doc.splitTextToSize(
        v.remark || "N/A",
        contentWidth * 0.55 - remarkLabelWidth,
      );
      doc.text(
        remarkLines[0] || "N/A",
        contentX + 7 + remarkLabelWidth,
        footerY + 5,
      );

      doc.setFont("helvetica", "normal").setFontSize(6.5);
      doc.setTextColor(...grayText);
      doc.text("Authorized Signature", headerRightX - 6, footerY + 5, {
        align: "right",
      });

      if (i < visitor.length - 1) {
        const cutY = y + passHeight + gapY / 2;
        doc.setDrawColor(190, 190, 190);
        doc.setLineWidth(0.2);
        doc.setLineDashPattern([1, 1], 0);
        doc.line(x, cutY, x + passWidth, cutY);
        doc.setLineDashPattern([], 0);

        doc.setFont("helvetica", "normal").setFontSize(6.5);
        const label = "cut here";
        const labelWidth = doc.getTextWidth(label) + 4;
        doc.setFillColor(255, 255, 255);
        doc.rect(
          x + passWidth / 2 - labelWidth / 2,
          cutY - 2,
          labelWidth,
          4,
          "F",
        );
        doc.setTextColor(170, 170, 170);
        doc.text(label, x + passWidth / 2, cutY + 0.8, { align: "center" });
      }
    }

    window.open(doc.output("bloburl"), "_blank");
  };

  const handleDownloadExcel = (type) => {
    let filteredData = [];

    if (type === "single") {
      const selectedDate = dateInputRef.current?.value;
      if (!selectedDate) {
        toast.warning("Please select a date first");
        return;
      }

      filteredData = visitorBookDetails.filter(
        (v) =>
          new Date(v.createdAt).toISOString().split("T")[0] === selectedDate,
      );
    } else if (type === "multiple") {
      const fromDate = fromDateInputRef.current?.value;
      const toDate = toDateInputRef.current?.value;
      if (!fromDate || !toDate) {
        toast.warning("Please select both From and To dates");
        return;
      }

      const from = new Date(fromDate);
      const to = new Date(toDate);
      filteredData = visitorBookDetails.filter((v) => {
        const visitDate = new Date(v.createdAt);
        return visitDate >= from && visitDate <= to;
      });
    }

    if (filteredData.length === 0) {
      toast.info("No records found for the selected date(s)");
      return;
    }

    // Prepare data for Excel
    const exportData = filteredData.map((v, i) => ({
      "S.No": i + 1,
      "Visitor ID": v.visitorId || "",
      Name: v.name || "",
      "Phone No": v.phoneNo || "",
      "Email ID": v.emailId || "",
      visitorType: v.visitorType || "",
      Purpose: v.purpose || "",
      admissionNo: v.admissionNo || "",
      "Meeting With": v.meetingWith || "",
      "No. Of Persons": v.numberOfPersons || "",
      "In Time": v.inTime || "",
      "Out Time": v.outTime || "",
      "Document URL": v.document || "",
      "Webcam URL": v.webcam || "",
      Date: new Date(v.createdAt).toLocaleString("en-GB"),
    }));

    // Create a worksheet and workbook
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Visitor Report");

    // Generate Excel file
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const data = new Blob([excelBuffer], { type: "application/octet-stream" });

    const fileName =
      type === "single"
        ? `Visitor_Report_${dateInputRef.current.value}.xlsx`
        : `Visitor_Report_${fromDateInputRef.current.value}_to_${toDateInputRef.current.value}.xlsx`;

    saveAs(data, fileName);
    toast.success("Report Generated");
  };

  // Excel sheet names: max 31 chars, can't contain \ / ? * [ ] :
  const sanitizeSheetName = (name) => {
    let clean = (name || "Unspecified")
      .toString()
      .replace(/[\\/?*[\]:]/g, "-")
      .trim();
    if (!clean) clean = "Unspecified";
    if (clean.length > 31) clean = clean.substring(0, 31);
    return clean;
  };

  const buildExportRow = (v, i) => ({
    "S.No": i + 1,
    "Visitor ID": v.visitorId || "",
    Name: v.name || "",
    "Phone No": v.phoneNo || "",
    "Email ID": v.emailId || "",
    "Visitor Type": v.visitorType || "",
    Purpose: v.purpose || "",
    "Admission No": v.admissionNo || "",
    "Meeting With": v.meetingWith || "",
    "No. Of Persons": v.numberOfPersons || "",
    "In Time": v.inTime || "",
    "Out Time": v.outTime || "",
    "Document URL": v.document || "",
    "Webcam URL": v.webcam || "",
    Date: v.createdAt ? new Date(v.createdAt).toLocaleString("en-GB") : "",
  });

  // Simple, single-sheet reports
  const handleDownloadVisitorBookReport = () => {
    if (visitorBookDetails.length === 0) {
      toast.info("No records found");
      return;
    }
    const exportData = visitorBookDetails.map((v, i) => buildExportRow(v, i));
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Visitor Book Report");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const data = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(
      data,
      `Visitor_Book_Report_${new Date().toISOString().split("T")[0]}.xlsx`,
    );
    toast.success("Report Generated");
  };

  const handleDownloadTodayReport = () => {
    const today = new Date().toISOString().split("T")[0];
    const filteredData = visitorBookDetails.filter(
      (v) => new Date(v.createdAt).toISOString().split("T")[0] === today,
    );

    if (filteredData.length === 0) {
      toast.info("No visitors recorded today");
      return;
    }

    const exportData = filteredData.map((v, i) => buildExportRow(v, i));
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Today's Visitors");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const data = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(data, `Todays_Visitor_Report_${today}.xlsx`);
    toast.success("Report Generated");
  };

  // Grouped, multi-sheet report (used for Meeting With + Visitor Type)
  const handleDownloadGroupedReport = (
    groupByField,
    summaryLabel,
    fileNamePrefix,
  ) => {
    if (visitorBookDetails.length === 0) {
      toast.info("No records found");
      return;
    }

    // Group records by the chosen field (meetingWith / visitorType)
    const groups = visitorBookDetails.reduce((acc, v) => {
      const rawKey = v[groupByField];
      const key = rawKey && rawKey.trim() ? rawKey.trim() : "Unspecified";
      if (!acc[key]) acc[key] = [];
      acc[key].push(v);
      return acc;
    }, {});

    const workbook = XLSX.utils.book_new();

    // Summary sheet first, listing each group with its count
    const summaryData = Object.entries(groups).map(([key, items]) => ({
      [summaryLabel]: key,
      "Total Visitors": items.length,
    }));
    const summarySheet = XLSX.utils.json_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary");

    // One sheet per group, with duplicate-name safety (sanitized names can collide)
    const usedNames = new Set(["summary"]);
    Object.entries(groups).forEach(([key, items]) => {
      const exportData = items.map((v, i) => buildExportRow(v, i));
      const worksheet = XLSX.utils.json_to_sheet(exportData);

      let sheetName = sanitizeSheetName(key);
      let finalName = sheetName;
      let counter = 1;
      while (usedNames.has(finalName.toLowerCase())) {
        finalName = sanitizeSheetName(
          `${sheetName.substring(0, 27)}_${counter}`,
        );
        counter++;
      }
      usedNames.add(finalName.toLowerCase());

      XLSX.utils.book_append_sheet(workbook, worksheet, finalName);
    });

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const data = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(
      data,
      `${fileNamePrefix}_${new Date().toISOString().split("T")[0]}.xlsx`,
    );
    toast.success("Report Generated");
  };

  // Filter
  const filteredRecords = visitorBookDetails.filter((gateDetails) => {
    const searchLower = searchTerm.toLowerCase();

    return Object.values(gateDetails || {}).some(
      (value) =>
        typeof value === "string" && value.toLowerCase().includes(searchLower),
    );
  });

  // Calculate pagination
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredRecords.slice(
    indexOfFirstRecord,
    indexOfLastRecord,
  );
  const totalPages = Math.ceil(filteredRecords.length / recordsPerPage);

  // Page Navigation
  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  return (
    <>
      <div id="db-wrapper">
        <main id="page-content">
          <div
            class="modal fade gd-example-modal-lg"
            tabindex="-1"
            role="dialog"
            aria-labelledby="myLargeModalLabel"
            aria-hidden="true"
          >
            <div class="modal-dialog modal-xl">
              <div class="modal-content">
                <form onSubmit={handleSubmit}>
                  <div class="modal-header d-flex justify-content-between">
                    <h5 class="modal-title fs-4" id="exampleModalToggleLabel">
                      Add Visitor{" "}
                      <span className="ms-3 text-secondary">(05/08)</span>
                    </h5>
                    <div className="d-flex align-items-center">
                      <span class="badge bg-success-soft me-3 px-3">
                        Editable
                      </span>
                      <div class="form-check form-switch me-4">
                        <input
                          type="checkbox"
                          class="form-check-input m-0"
                          checked
                        />
                      </div>
                      <button
                        type="button"
                        class="btn-close"
                        data-bs-dismiss="modal"
                        aria-label="Close"
                      ></button>
                    </div>
                  </div>

                  <div class="modal-body">
                    <div className="row g-2 mb-2">
                      <div className="col-8">
                        <div className="row g-2">
                          <div className="col-lg-6 col-12">
                            <label className="form-label mb-1 ms-3">
                              Name <span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              name="name"
                              className="form-control text-dark"
                              placeholder="Enter Name"
                              value={formData.name}
                              onChange={handleChange}
                              required
                            />
                          </div>
                          <div className="col-lg-6 col-12">
                            <label className="form-label mb-1 ms-3">
                              Phone No <span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              name="phoneNo"
                              className="form-control text-dark"
                              placeholder="Enter Phone No"
                              value={formData.phoneNo}
                              onChange={handleChange}
                              required
                            />
                          </div>
                          <div className="col-lg-6 col-12">
                            <label className="form-label mb-1 ms-3">
                              Email ID
                            </label>
                            <input
                              type="emailId"
                              name="emailId"
                              className="form-control text-dark"
                              placeholder="Enter Email ID"
                              value={formData.emailId}
                              onChange={handleChange}
                            />
                          </div>
                          <div className="col-lg-6 col-12">
                            <label className="form-label mb-1 ms-3">
                              Visitor Type{" "}
                              <span className="text-danger">*</span>
                            </label>
                            <Select
                              name="visitorType"
                              value={
                                visitorTypeOptions.find(
                                  (opt) => opt.value === formData.visitorType,
                                ) || null
                              }
                              onChange={(selected) =>
                                handleChange({
                                  target: {
                                    name: "visitorType",
                                    value: selected?.value || "",
                                  },
                                })
                              }
                              options={visitorTypeOptions}
                              placeholder="Select Visitor Type"
                            />
                          </div>
                          <div className="col-lg-6 col-12">
                            <label className="form-label mb-1 ms-3">
                              Purpose <span className="text-danger">*</span>
                            </label>
                            <Select
                              name="purpose"
                              value={
                                purposeOptions.find(
                                  (opt) => opt.value === formData.purpose,
                                ) || null
                              }
                              onChange={(selected) =>
                                handleChange({
                                  target: {
                                    name: "purpose",
                                    value: selected?.value || "",
                                  },
                                })
                              }
                              options={purposeOptions}
                              placeholder="Select Purpose"
                            />
                          </div>
                          {isPickupPurpose ? (
                            <div className="col-lg-6 col-12">
                              <div className="row g-2">
                                <div className="col-lg-7">
                                  <label className="form-label mb-1 ms-3">
                                    Admission No.{" "}
                                    <span className="text-danger">*</span>
                                  </label>
                                  <input
                                    type="text"
                                    name="admissionNo"
                                    className="form-control text-dark"
                                    placeholder="Enter Admission No."
                                    value={formData.admissionNo}
                                    onChange={handleChange}
                                    maxLength={5}
                                    required
                                  />
                                </div>

                                <div className="col-lg-5 d-flex align-items-end ps-3">
                                  {studentInfoLoading ? (
                                    <small className="text-secondary">
                                      ...
                                    </small>
                                  ) : studentInfoError ? (
                                    <small className="text-danger">
                                      {studentInfoError}
                                    </small>
                                  ) : studentName ? (
                                    <small className="text-success fs-5 fw-medium lh-1">
                                      {studentName}{" "}
                                      <i className="bi bi-check-circle-fill ms-1"></i>
                                    </small>
                                  ) : null}
                                </div>
                              </div>
                            </div>
                          ) : null}
                          <div className="col-lg-6 col-12">
                            <label className="form-label mb-1 ms-3">
                              Meeting With{" "}
                              <span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              name="meetingWith"
                              className="form-control text-dark"
                              placeholder="Enter Meeting With"
                              value={formData.meetingWith}
                              onChange={handleChange}
                              required
                            />
                          </div>
                          <div className="col-lg-6 col-12">
                            <label className="form-label mb-1 ms-3">
                              Number Of Person{" "}
                              <span className="text-danger">*</span>
                            </label>
                            <input
                              type="number"
                              name="numberOfPersons"
                              className="form-control text-dark"
                              placeholder="Enter Number Of Person"
                              value={formData.numberOfPersons}
                              onChange={handleChange}
                              required
                            />
                          </div>
                          <div className="col-lg-6 col-12">
                            <label className="form-label mb-1 ms-3">
                              In Time <span className="text-danger">*</span>
                            </label>
                            <input
                              type="time"
                              name="inTime"
                              className="form-control text-dark"
                              value={formData.inTime}
                              onChange={handleChange}
                              required
                            />
                          </div>
                          <div className="col-lg-6 col-12">
                            <label className="form-label mb-1 ms-3">
                              Document
                            </label>
                            <input
                              type="file"
                              className="form-control text-dark"
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  document: e.target.files[0],
                                })
                              }
                            />
                          </div>
                          <div className="col-12">
                            <label class="form-label mb-1 ms-3">
                              Remark <span className="text-danger">*</span>
                            </label>
                            <textarea
                              name="remark"
                              class="form-control text-dark"
                              placeholder="Enter Remark / Comment"
                              value={formData.remark}
                              onChange={handleChange}
                              required
                              rows="2"
                            ></textarea>
                          </div>
                        </div>
                      </div>
                      <div className="col-4 d-flex align-items-center pe-4 ps-7">
                        <div className="text-center">
                          <h4 className="mb-0 mt-3 normal-text lh-1 text-center">
                            <span className="text-dark ls-sm fs-4">
                              Upload webcam Photos
                            </span>
                          </h4>
                          <p className="m-0 fs-5 ls-sm text-center text-dark opacity-75 lh-1 mt-2 mb-4">
                            Upload a webcam image to instantly see photo
                            <br />
                            you’re in—no tags, no search.
                          </p>
                          {showCamera ? (
                            <div className="position-relative">
                              <video
                                id="visitorVideo"
                                autoPlay
                                playsInline
                                ref={(video) => {
                                  if (video && cameraStream)
                                    video.srcObject = cameraStream;
                                }}
                                width="200"
                                height="200"
                                className="border rounded-circle object-fit-cover"
                              ></video>
                              <div className="d-flex justify-content-center align-items-center gap-1 position-relative z-5 mt-3">
                                <span
                                  className="py-1 bg-info-soft px-5 rounded-pill fw-normal cursor-pointer"
                                  onClick={capturePhoto}
                                >
                                  Capture
                                </span>
                                <span
                                  className="py-1 bg-danger-soft px-5 rounded-pill fw-normal cursor-pointer"
                                  onClick={closeWebcam}
                                >
                                  Close
                                </span>
                              </div>
                              <p className="m-0 fs-5 ls-sm text-center text-dark opacity-75 lh-1 mt-2">
                                Upload a webcam image to instantly see photo
                                <br />
                                you’re in—no tags, no search.
                              </p>
                            </div>
                          ) : (
                            <img
                              src={
                                capturedImage ||
                                "/assets/images/webcam-icon.png"
                              }
                              alt="Captured"
                              className="rounded-circle border object-fit-cover cursor-pointer"
                              width="200"
                              height="200"
                              onClick={openWebcam}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="modal-footer d-flex justify-content-between py-2">
                    <div>
                      <button
                        type="button"
                        class="btn btn-secondary px-3 me-1 rounded-pill"
                        data-bs-dismiss="modal"
                      >
                        <i className="bi bi-x-lg"></i>
                      </button>
                      <button
                        type="submit"
                        class="btn btn-primary px-5 rounded-pill fw-normal"
                        disabled={loading}
                      >
                        {loading ? (
                          <div
                            class="spinner-border spinner-border-sm"
                            role="status"
                          ></div>
                        ) : (
                          "Submit"
                        )}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>

          <div class="container-fluid my-3 px-3">
            <div className="row g-2">
              <div className="col-lg-8">
                <div class="table-responsive border-0 overflow-y-hidden bg-white rounded-4">
                  <table class="table mb-0 text-nowrap table-centered">
                    <thead class="table-light">
                      <tr>
                        <th className="w-10">No.</th>
                        <th className="lh-1 w-60">
                          Details
                          <i className="bi bi-info-circle ms-2 text-dark"></i>
                          <br />
                          <span className="fs-8 fw-normal text-secondary">
                            Displaying Information as per Record
                          </span>
                        </th>
                        <th className="lh-1 w-30">
                          Action
                          <i className="bi bi-person-gear ms-2 text-dark"></i>
                          <br />
                          <span className="fs-8 fw-normal text-secondary">
                            Role-Specific Action Panel
                          </span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentRecords.map((visitor, index) => (
                        <tr key={index}>
                          <td>{indexOfFirstRecord + index + 1}.</td>
                          <td className="lh-sm py-4 text-wrap text-capitalize">
                            <span class="text-dark">Visitor ID</span> &mdash;{" "}
                            {visitor.visitorId}
                            <br />
                            <span class="text-dark">Name</span> &mdash;{" "}
                            <span className="text-capitalize">
                              {visitor.name
                                .split(" ")
                                .reduce(
                                  (acc, word) => {
                                    const lastLine = acc[acc.length - 1];
                                    const currentLineLength =
                                      lastLine.join(" ").length;
                                    if (
                                      currentLineLength + word.length + 1 >
                                      50
                                    ) {
                                      acc.push([word]);
                                    } else {
                                      lastLine.push(word);
                                    }
                                    return acc;
                                  },
                                  [[]],
                                )
                                .map((group, i) => (
                                  <span key={i}>
                                    {group.join(" ")}
                                    <br />
                                  </span>
                                ))}
                            </span>
                            <br />
                            <span class="text-dark">Phone No</span> &mdash;{" "}
                            {visitor.phoneNo} <br />
                            <span class="text-dark">Purpose</span> &mdash;{" "}
                            {visitor.purpose}
                            <br />
                            <span class="text-dark">
                              Meeting With
                            </span> &mdash; {visitor.meetingWith}
                            <br /> <span class="text-dark">
                              No. Of Person
                            </span>{" "}
                            &mdash; {visitor.numberOfPersons}
                            <br />
                            <span class="text-dark">In Time</span> &mdash;{" "}
                            {visitor.inTime}
                            <br />
                            <span class="text-dark">Out Time</span> &mdash;{" "}
                            {visitor.outTime}
                            <br />
                            <span className="text-dark">
                              Document
                            </span> &mdash;{" "}
                            {visitor?.document &&
                            visitor.document.length > 0 &&
                            visitor.document !== "[]" ? (
                              <a
                                href={visitor.document}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                Click Here
                              </a>
                            ) : (
                              <span className="text-muted">N/A</span>
                            )}
                            <br />
                            <br />
                            <span class="text-dark">Date</span> &mdash;{" "}
                            <span className="text-uppercase">
                              {new Date(visitor.createdAt).toLocaleString(
                                "en-GB",
                                {
                                  day: "2-digit",
                                  month: "2-digit",
                                  year: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  hour12: true,
                                },
                              )}
                            </span>
                            <br />
                            <span className="text-dark">
                              Webcam
                            </span> &mdash;{" "}
                            {visitor?.webcam &&
                            visitor.webcam.length > 0 &&
                            visitor.webcam !== "[]" ? (
                              <a
                                href={visitor.webcam}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                Click Here
                              </a>
                            ) : (
                              <span className="text-muted">N/A</span>
                            )}
                            <br />
                          </td>
                          <td>
                            <div className="d-flex flex-column gap-1 width-fit-content">
                              <span
                                onClick={() => handleDownloadPDF([visitor])}
                                class="badge bg-success-soft cursor-pointer px-4 rounded-3 btn-py-sm"
                              >
                                Download
                              </span>
                              <span class="badge bg-warning-soft cursor-pointer px-4 rounded-3 btn-py-sm">
                                Update
                              </span>
                              <div className="d-flex justify-content-center">
                                <hr className="w-50" />
                              </div>
                              <span class="badge bg-danger-soft cursor-pointer px-4 rounded-3 btn-py-sm">
                                Delete
                              </span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="col-lg-4">
                <div class="">
                  <div className="mb-3">
                    <div className="d-flex bg-white justify-content-between rounded-4">
                      <form
                        className="d-flex align-items-center"
                        onSubmit={(e) => e.preventDefault()}
                      >
                        <span className="position-absolute ps-3 search-icon">
                          <i className="bi bi-filter-circle-fill ms-1" />
                        </span>
                        <input
                          type="search"
                          className="form-control ps-7 rounded-3 border-0 rounded-4"
                          placeholder="Filter Data"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </form>
                      <Link
                        data-bs-toggle="modal"
                        data-bs-target=".gd-example-modal-lg"
                        className="btn btn-warning px-5 fs-6 fw-medium rounded-4 fit-content"
                      >
                        Add Visitor
                      </Link>
                    </div>
                    <div className="mt-2 ms-4">
                      <nav aria-label="breadcrumb">
                        <ol class="breadcrumb pe-3">
                          <li class="breadcrumb-item">
                            <a href="#">Front Office</a>
                          </li>
                          <li
                            class="breadcrumb-item active"
                            aria-current="page"
                          >
                            Visitor Book
                          </li>
                        </ol>
                      </nav>
                    </div>
                  </div>

                  <div className="p-4 bg-white rounded-4">
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="d-flex align-items-center">
                        {loading ? (
                          <div
                            className="spinner-border text-dark data-loader-spinner"
                            role="status"
                          ></div>
                        ) : (
                          <img src="/assets/images/folder.svg" />
                        )}

                        <p className="my-0 fw-semibold lh-1 fs-5 text-dark mx-3">
                          Visitor Book
                          <br />
                          <span className="text-secondary fs-6 fw-medium lh-sm">
                            0.4 MB Size
                          </span>
                        </p>
                      </div>
                      <span className="badge bg-pink rounded-3 px-4 py-2 me-1 mt-1 cursor-pointer">
                        Export
                      </span>
                    </div>

                    <div class="px-4 py-2 mt-3 bg-light rounded-4">
                      <li className="list-group-item px-0">
                        <a
                          className="d-flex align-items-center text-inherit mb-0 justify-content-between"
                          data-bs-toggle="collapse"
                          href="#specifications"
                          role="button"
                          aria-expanded="false"
                          aria-controls="specifications"
                        >
                          <p className="m-0 fw-medium lh-sm text-dark">
                            Download Report
                          </p>
                          <span className="chevron-arrow ms-4">
                            <i className="bi bi-chevron-down fs-4" />
                          </span>
                        </a>
                        <div
                          className="collapse"
                          id="specifications"
                          data-bs-parent="#ecommerceAccordion"
                        >
                          <div className="py-3">
                            <div class="rounded-4 d-flex flex-wrap">
                              <span
                                onClick={handleDownloadVisitorBookReport}
                                class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1 cursor-pointer"
                              >
                                Visitor Book Report
                              </span>
                              <span
                                onClick={handleDownloadTodayReport}
                                class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1 cursor-pointer"
                              >
                                Today's Visitor Report
                              </span>
                              <span
                                onClick={() =>
                                  handleDownloadGroupedReport(
                                    "meetingWith",
                                    "Meeting With",
                                    "Meeting_With_Wise_Report",
                                  )
                                }
                                class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1 cursor-pointer"
                              >
                                Meeting With &mdash; Wise Report
                              </span>
                              <span
                                onClick={() =>
                                  handleDownloadGroupedReport(
                                    "visitorType",
                                    "Visitor Type",
                                    "Visitor_Type_Wise_Report",
                                  )
                                }
                                class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1 cursor-pointer"
                              >
                                Visitor Type Wise Report
                              </span>
                            </div>
                          </div>
                        </div>
                      </li>
                    </div>

                    {(userRole?.includes("Master Admin") ||
                      userRole?.includes("Admin")) && (
                      <>
                        <div className="bg-secondary-soft p-2 rounded-top-4 mt-2 px-4">
                          <div className="row g-0 bg-white p-1 rounded-pill">
                            <div className="col-6">
                              <span
                                className={`btn btn-sm px-4 rounded-pill fw-medium border-0 w-100 ${
                                  activeReport === "single"
                                    ? "btn-dark"
                                    : "btn-transparent"
                                }`}
                                onClick={() => setActiveReport("single")}
                              >
                                Date Wise
                              </span>
                            </div>
                            <div className="col-6">
                              <span
                                className={`btn btn-sm px-4 rounded-pill fw-medium border-0 w-100 ${
                                  activeReport === "multiple"
                                    ? "btn-dark"
                                    : "btn-transparent"
                                }`}
                                onClick={() => setActiveReport("multiple")}
                              >
                                Multi-Date Wise
                              </span>
                            </div>
                          </div>
                        </div>

                        {activeReport === "single" && (
                          <div className="bg-secondary-soft rounded-bottom-4 py-3 px-4 mt-1">
                            <div>
                              <input
                                type="date"
                                className="form-control ps-4 pe-5 border-0 rounded-pill"
                                placeholder="..."
                                ref={dateInputRef}
                              />
                            </div>
                            <span
                              className="btn btn-primary btn-sm px-4 rounded-pill mt-2 me-1"
                              onClick={() => handleDownloadExcel("single")}
                            >
                              Download Report
                            </span>
                          </div>
                        )}

                        {activeReport === "multiple" && (
                          <div className="bg-secondary-soft rounded-bottom-4 py-3 px-4 mt-1">
                            <div>
                              <input
                                type="date"
                                className="form-control ps-4 pe-5 border-0 rounded-pill"
                                placeholder="..."
                                ref={fromDateInputRef}
                              />
                              <input
                                type="date"
                                className="form-control ps-4 pe-5 border-0 rounded-pill mt-1"
                                placeholder="..."
                                ref={toDateInputRef}
                              />
                            </div>
                            <span
                              className="btn btn-primary btn-sm px-4 rounded-pill mt-2 me-1"
                              onClick={() => handleDownloadExcel("multiple")}
                            >
                              Download Report
                            </span>
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  <div className="my-4">
                    <nav>
                      <ul className="pagination justify-content-center mb-0">
                        <li
                          className={`page-item ${
                            currentPage === 1 ? "disabled" : ""
                          }`}
                        >
                          <button className="page-link" onClick={prevPage}>
                            <i className="bi bi-chevron-left"></i>
                          </button>
                        </li>

                        {(() => {
                          const pageNumbers = [];
                          pageNumbers.push(1);

                          if (currentPage > 3) {
                            pageNumbers.push("left-ellipsis");
                          }

                          if (currentPage !== 1 && currentPage !== totalPages) {
                            pageNumbers.push(currentPage);
                          }

                          if (currentPage < totalPages - 2) {
                            pageNumbers.push("right-ellipsis");
                          }

                          if (totalPages > 1) {
                            pageNumbers.push(totalPages);
                          }

                          return pageNumbers.map((page, index) => {
                            if (
                              page === "left-ellipsis" ||
                              page === "right-ellipsis"
                            ) {
                              return (
                                <li key={index} className="page-item disabled">
                                  <span className="page-link">...</span>
                                </li>
                              );
                            }

                            return (
                              <li
                                key={index}
                                className={`page-item ${
                                  currentPage === page ? "active" : ""
                                }`}
                              >
                                <button
                                  className="page-link"
                                  onClick={() => setCurrentPage(page)}
                                >
                                  {page}
                                </button>
                              </li>
                            );
                          });
                        })()}

                        <li
                          className={`page-item ${
                            currentPage === totalPages ? "disabled" : ""
                          }`}
                        >
                          <button className="page-link" onClick={nextPage}>
                            <i className="bi bi-chevron-right"></i>
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
}

export default VisitorBook;
